import {
  publicationFixtures,
  rejectedPublication,
  failureLabels,
  type Publication,
} from "./publishing-data";
export type Role =
  | "SYSTEM_ADMIN"
  | "OPERATOR"
  | "BILLING_STAFF"
  | "STAFF_VIEWER"
  | "CUSTOMER_OWNER"
  | "CUSTOMER_ADMIN"
  | "CUSTOMER_MEMBER"
  | "CUSTOMER_VIEWER"
  | "MACHINE";
export type ServiceState = "active" | "overdue" | "suspended" | "terminated";
export type Scope = "admin" | "customer" | "machine";
export const roleNames: Record<Role, string> = {
  SYSTEM_ADMIN: "시스템 관리자",
  OPERATOR: "운영 담당자",
  BILLING_STAFF: "결제 담당자",
  STAFF_VIEWER: "관리자 · 열람",
  CUSTOMER_OWNER: "고객사 대표",
  CUSTOMER_ADMIN: "고객사 관리자",
  CUSTOMER_MEMBER: "고객사 회원",
  CUSTOMER_VIEWER: "고객사 · 열람",
  MACHINE: "머신 전용",
};
export const customers = [
  {
    id: "onyou",
    name: "온유의원",
    plan: "프로 · 월간",
    state: "active" as ServiceState,
    machines: 3,
    phones: 7,
    tasks: 842,
    limit: 1500,
    contact: "박*민 · 현장 담당",
    initials: "온",
  },
  {
    id: "moru",
    name: "모루식당",
    plan: "기본 · 월간",
    state: "overdue" as ServiceState,
    machines: 1,
    phones: 2,
    tasks: 186,
    limit: 300,
    contact: "김*서 · 대표",
    initials: "모",
  },
  {
    id: "bom",
    name: "봄날스튜디오",
    plan: "기본 · 월간",
    state: "suspended" as ServiceState,
    machines: 1,
    phones: 3,
    tasks: 92,
    limit: 300,
    contact: "이*윤 · 결제 담당",
    initials: "봄",
  },
];
export type Customer = (typeof customers)[number];
export const serviceNames: Record<ServiceState, string> = {
  active: "정상 이용",
  overdue: "결제 유예",
  suspended: "서비스 정지",
  terminated: "서비스 해지",
};
export function isStaff(role: Role) {
  return ["SYSTEM_ADMIN", "OPERATOR", "BILLING_STAFF", "STAFF_VIEWER"].includes(
    role,
  );
}
export type Permission =
  | "manuscriptRead"
  | "manuscriptWrite"
  | "rolesRead"
  | "rolesWrite"
  | "create"
  | "command"
  | "pair"
  | "detail"
  | "pay"
  | "billing"
  | "member"
  | "marketing"
  | "connect"
  | "usage"
  | "audit"
  | "settings"
  | "publishRead"
  | "publishWrite";
const grants: Record<Permission, Role[]> = {
  manuscriptRead: ["SYSTEM_ADMIN", "OPERATOR", "STAFF_VIEWER"],
  manuscriptWrite: ["SYSTEM_ADMIN", "OPERATOR"],
  rolesRead: [
    "SYSTEM_ADMIN",
    "OPERATOR",
    "BILLING_STAFF",
    "STAFF_VIEWER",
    "CUSTOMER_OWNER",
    "CUSTOMER_ADMIN",
  ],
  rolesWrite: ["SYSTEM_ADMIN", "CUSTOMER_OWNER", "CUSTOMER_ADMIN"],
  publishRead: [
    "CUSTOMER_OWNER",
    "CUSTOMER_ADMIN",
    "CUSTOMER_MEMBER",
    "CUSTOMER_VIEWER",
  ],
  publishWrite: ["CUSTOMER_OWNER", "CUSTOMER_ADMIN"],
  create: ["SYSTEM_ADMIN", "OPERATOR"],
  command: ["SYSTEM_ADMIN", "OPERATOR"],
  pair: ["SYSTEM_ADMIN", "OPERATOR"],
  detail: ["SYSTEM_ADMIN", "OPERATOR", "STAFF_VIEWER"],
  pay: ["SYSTEM_ADMIN", "BILLING_STAFF", "CUSTOMER_OWNER"],
  billing: [
    "SYSTEM_ADMIN",
    "OPERATOR",
    "BILLING_STAFF",
    "STAFF_VIEWER",
    "CUSTOMER_OWNER",
    "CUSTOMER_ADMIN",
  ],
  member: ["SYSTEM_ADMIN", "OPERATOR", "CUSTOMER_OWNER", "CUSTOMER_ADMIN"],
  marketing: [
    "SYSTEM_ADMIN",
    "OPERATOR",
    "STAFF_VIEWER",
    "CUSTOMER_OWNER",
    "CUSTOMER_ADMIN",
    "CUSTOMER_MEMBER",
    "CUSTOMER_VIEWER",
  ],
  connect: ["SYSTEM_ADMIN", "OPERATOR"],
  usage: ["SYSTEM_ADMIN", "OPERATOR", "BILLING_STAFF", "STAFF_VIEWER"],
  audit: [
    "SYSTEM_ADMIN",
    "OPERATOR",
    "BILLING_STAFF",
    "STAFF_VIEWER",
    "CUSTOMER_OWNER",
    "CUSTOMER_ADMIN",
  ],
  settings: ["SYSTEM_ADMIN", "OPERATOR", "CUSTOMER_OWNER", "CUSTOMER_ADMIN"],
};
export function permitted(
  role: Role,
  permission: Permission,
  state: ServiceState = "active",
) {
  if (!grants[permission].includes(role)) return false;
  if (permission === "rolesWrite" && !["active", "overdue"].includes(state))
    return false;
  if (
    [
      "create",
      "command",
      "pair",
      "member",
      "connect",
      "settings",
      "publishWrite",
    ].includes(permission) &&
    !["active", "overdue"].includes(state)
  )
    return isStaff(role) && permission !== "create";
  return true;
}
export const runFixtures = [
  ...[rejectedPublication, ...publicationFixtures].map((p) => ({
    id: p.runId,
    customer: p.execution,
    targetCustomer: p.target,
    publication: p,
    title: p.title,
    keywords:
      p.target === "onyou" ? ["강남 내과", "건강검진 예약"] : ["가을 메뉴"],
    kind: "디바이스 작업",
    status: p.failure ? failureLabels[p.failure] : "성공",
    job: "JOB-PUBLISH · 일회성",
    time: p.postedAt === "게시 전" ? "실행 전" : p.postedAt.split(" ")[1],
    attempt: 1,
    detail: p.reason,
    device: p.machine,
    progress: p.stage === "done" ? 100 : 60,
  })),
  {
    id: "RUN-1048",
    targetCustomer: "onyou",
    publication: {
      ...publicationFixtures[0],
      id: "PUB-RUNNING",
      machine: "서울 접수 PC",
      runId: "RUN-1048",
      execution: "onyou",
      target: "onyou",
      stage: "run" as const,
      failure: undefined,
      verdict: "일치" as const,
      reason:
        "원고 검토 통과. 본문과 첨부를 입력하고 있으며 완료 검토는 아직 시작하지 않았습니다.",
      postedAt: "게시 전",
      url: undefined,
      captures: [],
    },
    customer: "onyou",
    title: "가을 진료 안내 블로그 발행",
    keywords: ["강남 내과", "가을 건강관리", "건강검진 예약"],
    kind: "디바이스 작업",
    status: "진행 중",
    job: "JOB-021 · 매일 14:00",
    time: "14:10:00",
    attempt: 1,
    detail: "사진 첨부 후 본문 확인 중",
    device: "블로그 01",
    progress: 62,
  },
  {
    id: "RUN-1047",
    customer: "moru",
    title: "검색 노출 지표 수집",
    kind: "수집",
    status: "최종 실패",
    job: "JOB-018 · 매일 09:00 / 15:00",
    time: "14:05:00",
    attempt: 3,
    detail: "Google 연결이 만료되었습니다. 계정을 다시 연결하세요.",
    device: "코어 수집기",
    progress: 0,
  },
  {
    id: "RUN-1046",
    customer: "onyou",
    title: "진료 시간 안내 발행",
    keywords: ["내과 진료 시간"],
    kind: "디바이스 작업",
    status: "사람 확인 필요",
    job: "JOB-019 · 일회성",
    time: "13:48:00",
    attempt: 1,
    detail: "네이버 로그인이 필요합니다. 현장에서 디바이스를 확인하세요.",
    device: "블로그 02",
    progress: 0,
  },
  {
    id: "RUN-1045",
    customer: "bom",
    title: "주간 콘텐츠 발행",
    kind: "디바이스 작업",
    status: "보류",
    job: "JOB-012 · 매주 목요일",
    time: "13:30:00",
    attempt: 0,
    detail: "서비스 정지로 배정을 보류했습니다.",
    device: "촬영실 PC",
    progress: 0,
  },
  {
    id: "RUN-1044",
    customer: "onyou",
    title: "10월 청구서 발행",
    kind: "청구",
    status: "성공",
    job: "JOB-003 · 매월 15일",
    time: "09:00:00",
    attempt: 1,
    detail: "청구서 생성과 결제 안내를 완료했습니다.",
    device: "코어 청구 배치",
    progress: 100,
  },
];
export type Run = (typeof runFixtures)[number] & {
  keywords?: string[];
  targetCustomer?: string;
  publication?: Publication;
  postTitle?: string;
  llmModel?: string;
  customerRequestId?: string;
};
export function tone(status: string) {
  return ["실패", "최종 실패", "서비스 정지", "서비스 해지", "잠금"].includes(
    status,
  )
    ? "coral"
    : [
          "결제 유예",
          "보류",
          "승인 대기",
          "확인 필요",
          "검토 반려",
          "사람 확인 필요",
          "결과 불명",
          "검토 불일치",
        ].includes(status)
      ? "amber"
      : ["진행 중", "대기", "요청 대기"].includes(status)
        ? "blue"
        : "teal";
}
